import React from "react";
import { toast } from "react-toastify";
import { Badge, Box, Button, Divider, Typography } from "@mui/material";
import { Add, ConfirmationNumberOutlined } from "@mui/icons-material";

import { useTicketDetailContext } from "contexts/TicketDetailContext";

import FormDrawer from "components/Shared/FormDrawer";
import ResponseForm from "components/Response/ResponseForm";
import ResponseList from "components/Response/ResponseList";

import {
  useAddTicketResponseMutation,
  useUpdateTicketResponseMutation,
} from "store/services/ticketApi";

const TicketResponsesTab = () => {
  const [showResponseForm, setShowResponseForm] = React.useState(false);
  const [selectedResponse, setSelectedResponse] = React.useState(null);

  const { ticketDetail } = useTicketDetailContext();

  const disableTicketResponse = ticketDetail?.stage === "Closed";

  // RTK Query
  const [addResponse, { isLoading: addingResponse }] =
    useAddTicketResponseMutation();

  const [updateResponse, { isLoading: updatingResponse }] =
    useUpdateTicketResponseMutation();

  // methods
  const handleCancelResponse = () => {
    setShowResponseForm(false);
    setSelectedResponse(null);
  };

  const handleAddResponse = async (data) => {
    try {
      await addResponse({ ...data, ticketId: ticketDetail?._id }).unwrap();
      toast.success("Response successfully added!");
      handleCancelResponse();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't add response!");
    }
  };

  const handleEditResponse = (response) => {
    setShowResponseForm(true);
    setSelectedResponse(response);
  };

  const handleUpdateResponse = async (data) => {
    try {
      await updateResponse({
        ...data,
        ticketId: ticketDetail?._id,
        _id: selectedResponse?._id,
      }).unwrap();
      toast.success("Response successfully updated!");
      handleCancelResponse();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update response!");
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" padding={3}>
        <Box display="inline-flex" alignItems="center" gap={1.5}>
          <Badge badgeContent={ticketDetail?.responses?.length} color="primary">
            <ConfirmationNumberOutlined style={{ color: "gray" }} />
          </Badge>

          <Typography style={{ fontWeight: 500 }}>Responses</Typography>
        </Box>

        {!showResponseForm && (
          <Button
            startIcon={<Add />}
            variant="outlined"
            disabled={disableTicketResponse}
            onClick={() => setShowResponseForm(true)}
          >
            Response
          </Button>
        )}
      </Box>

      <Divider />

      <ResponseList
        responses={ticketDetail?.responses}
        onEditAction={handleEditResponse}
      />

      <FormDrawer
        title="Ticket Response"
        open={showResponseForm}
        onClose={handleCancelResponse}
      >
        <ResponseForm
          isBusy={addingResponse || updatingResponse}
          response={selectedResponse}
          onSubmit={(data) =>
            selectedResponse
              ? handleUpdateResponse(data)
              : handleAddResponse(data)
          }
          onCancel={handleCancelResponse}
        />
      </FormDrawer>
    </>
  );
};

export default TicketResponsesTab;
